import { useState } from "react";
import { AuthLayout } from "../../components/AuthLayout";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { sendOtpApi, verifyOtpApi } from "services/auth/authApis";
// shadcn UI components

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Mail } from "lucide-react";


// Email validation helper
const validateEmail = (email: string): boolean => {
  return (
    email.endsWith("@muj.manipal.edu") || email.endsWith("@jaipur.manipal.edu")
  );
};

// Zod schemas
const emailSchema = z.object({
  email: z
    .string()
    .email()
    .refine((val) => validateEmail(val), {
      message: "Email must end with @muj.manipal.edu or @jaipur.manipal.edu",
    }),
});

const otpSchema = z.object({
  otp: z.string().regex(/^\d{6}$/, {
    message: "OTP must be exactly 6 digits",
  }),
});

export const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [otpVisible, setOtpVisible] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [otpError, setOtpError] = useState("");
  const navigate = useNavigate();

  const handleSendMail = async () => {
    // Validate email with Zod
    const result = emailSchema.safeParse({ email });

    if (!result.success) {
      const errorMessage = result.error.issues[0]?.message || "Invalid email";
      setEmailError(errorMessage);
      toast.error(errorMessage);
      return;
    }

    setEmailError("");

    const data = await sendOtpApi(email);
    if (!data) {
      toast.error("some error occurred");
    } else {
      setOtpVisible(true);
    }
  };

  const handleCheckOTP = async () => {
    // Validate OTP with Zod
    const result = otpSchema.safeParse({ otp });

    if (!result.success) {
      const errorMessage = result.error.issues[0]?.message || "Invalid OTP";
      setOtpError(errorMessage);
      toast.error(errorMessage);
      return;
    }

    setOtpError("");

    const data = await verifyOtpApi(email, otp);
    if (data != null) {
      navigate("/update-password", { state: { email, token: data } });
    } else {
      toast.error("invalid OTP, try again");
      setOtp("");
    }
  };

  return (
    <AuthLayout>
      <div className="w-full">
        <div className="text-center space-y-1 mb-6">
          <p className="text-richblack-600 text-lg font-semibold mt-2">
            Forgot Password
          </p>
        </div>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            otpVisible ? handleCheckOTP() : handleSendMail();
          }}
        >
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <div className="relative mt-2">
              <Mail
              className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-500"
              size={22}
              />
              
              <Input
              id="email"
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setEmailError("");
              }}
              required
              className={`h-14 pl-14 pr-6 text-base ${
                emailError ? "border-red-500" : ""
              }`}
              />
              </div>
            {emailError && (
              <p className="text-sm text-red-500 mt-1">{emailError}</p>
            )}
          </div>
          {!otpVisible && (
          <>
            <Button
            type="submit"
            className="w-full mt-2 hover:bg-amber-500 text-white font-semibold py-2 rounded-md transition duration-200"
            >
              Send OTP
            </Button>
            <Button
            type="button"
            onClick={() => navigate("/login")}
            className="w-full mt-2 hover:bg-amber-500 text-white font-semibold py-2 rounded-md transition duration-200"
            >
              Back to Login
            </Button>
          </>
            
              )}
              {otpVisible && (
                <>
                <div className="space-y-3">
                  <p className="text-sm text-gray-600 text-center">
                    Enter the 6-digit code sent to your email
                    </p>
                  <div className="flex justify-center gap-3">
                  {[0, 1, 2, 3, 4, 5].map((index) => (
                  <Input
                  key={index}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={otp[index] || ""}
                  onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "");
                  const newOtp = otp.split("");
                  newOtp[index] = value;
                  setOtp(newOtp.join(""));
                  setOtpError("");
                  if (value && index < 5) {
                    document
                .getElementById(`otp-${index + 1}`)
                ?.focus();
              }
                }}
              onKeyDown={(e) => {
              if (
              e.key === "Backspace" &&
                !otp[index] &&
              index > 0
                ) {
                document.getElementById(`otp-${index - 1}`)?.focus();
                  }
              }}
                id={`otp-${index}`}
              className={`w-12 h-12 text-center text-lg font-semibold ${
              otpError ? "border-red-500" : ""
                }`}
                />
              ))}
              </div>
          </div>
              
              <Button
              type="submit"
              className="w-full mt-2 hover:bg-amber-500 text-white font-semibold py-2 rounded-md transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={otp.length !== 6}
              >
                Submit OTP
              </Button>
              <Button
              type="button"
              onClick={() => navigate("/login")}
              className="w-full mt-2 hover:bg-amber-500 text-white font-semibold py-2 rounded-md transition duration-200"
              >
                Back to Login
              </Button>
            </>
          )}
        </form>
      </div>
    </AuthLayout>
  );
};